<script>
    import { onMount } from "svelte";

    import { user } from "../../stores";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        getFormatDateAndTime,
        getMinDateOfPayment,
        showError,
        showException,
    } from "../../Utils/utilFunctions.svelte";

    import { NotificationModal, DateInput } from "./index.svelte";

    export let row = {};
    export let type;
    export let onFinish = () => {};

    $: canUserEdit = $user.role == 100;

    let receivedDate = "",
        displayDate = "",
        newReceiveDate = "",
        transactionDate = "",
        typeOfPayment;

    let isUpdatingReceiveDate = false,
        isEditModalOpen = false;

    $: receivedDate = _getReceiveDate(row);
    $: displayDate = _getDisplayDate(row);

    $: isRefundPayment = row?.payments?.length
        ? row.payments[0].status == 4 || row.payments[0].status == 5
        : row?.status
          ? [4, 5].includes(row.status)
          : false;

    $: typeOfPayment = row?.payments?.length
        ? row.payments[0].status
        : row?.status
          ? row.status
          : null;

    // 1 - Received, 2 - Received Externally, 5 - Refunded
    // Currently allow received/refunded date change
    $: isPaymentReceivedOrRefunded = row?.payments?.length
        ? [1, 2, 5].includes(row.payments[0].status)
        : row?.status
          ? [1, 2, 5].includes(row.status)
          : false;

    onMount(() => {
        receivedDate = _getReceiveDate(row);
        displayDate = _getDisplayDate(row);
        transactionDate = _getTransactionDate(row);
    });

    const updateReceiveDate = async () => {
        isUpdatingReceiveDate = true;

        _syncNewReceiveDate();

        await updatePayment();

        closeEditModal();

        isUpdatingReceiveDate = false;
    };

    const updatePayment = async () => {
        let newPaymentObj = {};

        if (type == "ReceiptsTable") newPaymentObj = { ...row.payments[0] };
        else newPaymentObj = { ...row };

        await apiMethods.putMethod
            .updatePayment({ ...newPaymentObj })
            .then((res) => {
                if (res.error) showError(res.error);
                else onFinish();
            })
            .catch((err) => showException(err));
    };

    const openEditModal = () => {
        isEditModalOpen = true;
        newReceiveDate = receivedDate;
    };

    const closeEditModal = () => {
        isEditModalOpen = false;
        newReceiveDate = "";
    };

    // HELPER FUNCTIONS
    const _getReceiveDate = (rowData) => {
        switch (type) {
            case "ReceiptsTable":
                return _getReceiveDateFromArr(rowData.payments);
            case "EstimatePaymentTable":
            case "InvoicePaymentTable":
            case "PatientEncounterPaymentTable":
                return _getReceiveDateFromStr(rowData.receivedDate);
            default:
                return "";
        }
    };

    const _getTransactionDate = (rowData) => {
        switch (type) {
            case "ReceiptsTable":
                return _getTransactionDateFromArr(rowData.payments);
            case "EstimatePaymentTable":
            case "InvoicePaymentTable":
            case "PatientEncounterPaymentTable":
                return _getTransactionDateFromStr(rowData.transactionDate);
            default:
                return "";
        }
    };

    const _getDisplayDate = (rowData) => {
        switch (type) {
            case "ReceiptsTable":
                return _getDisplayDateFromArr(rowData.payments);
            case "EstimatePaymentTable":
            case "InvoicePaymentTable":
            case "PatientEncounterPaymentTable":
                return _getDisplayDateFromStr(rowData.receivedDate);
            default:
                return "";
        }
    };

    const _syncNewReceiveDate = () => {
        switch (type) {
            case "ReceiptsTable":
                row.payments[0].receivedDate = newReceiveDate;
                return;
            case "EstimatePaymentTable":
            case "InvoicePaymentTable":
            case "PatientEncounterPaymentTable":
                row.receivedDate = newReceiveDate;
                return;
            default:
                return;
        }
    };

    const _getReceiveDateFromArr = (paymentsArr = []) => {
        if (
            !paymentsArr.length ||
            !paymentsArr[0].receivedDate ||
            paymentsArr[0].receivedDate == "0001-01-01T00:00:00"
        )
            return "-";

        return paymentsArr[0].receivedDate;
    };

    const _getDisplayDateFromArr = (paymentsArr = []) => {
        if (
            !paymentsArr.length ||
            !paymentsArr[0].receivedDate ||
            paymentsArr[0].receivedDate == "0001-01-01T00:00:00"
        )
            return "-";

        const dateArr = paymentsArr[0].receivedDate.split("T")[0].split("-");

        if (Number(dateArr[0]) < 1900) return "-";

        return getFormatDateAndTime(paymentsArr[0].receivedDate);
    };

    const _getReceiveDateFromStr = (dateStr = "") => {
        if (!dateStr || dateStr == "0001-01-01T00:00:00") return "-";

        return dateStr;
    };

    const _getDisplayDateFromStr = (dateStr = "") => {
        if (!dateStr || dateStr == "0001-01-01T00:00:00") return "-";

        return getFormatDateAndTime(dateStr);
    };

    const _getTransactionDateFromArr = (paymentsArr = []) => {
        if (
            !paymentsArr.length ||
            !paymentsArr[0].transactionDate ||
            paymentsArr[0].transactionDate == "0001-01-01T00:00:00"
        )
            return null;

        return paymentsArr[0].transactionDate;
    };

    const _getTransactionDateFromStr = (dateStr = "") => {
        if (!dateStr || dateStr == "0001-01-01T00:00:00") return null;

        return dateStr;
    };
</script>

<div class="flex flex-row items-center justify-start">
    {#if !canUserEdit || displayDate == "-"}
        <span class="text-left cursor-default">{displayDate}</span>
    {:else}
        <span
            class="text-left cursor-pointer underline"
            on:click|preventDefault={openEditModal}
        >
            {displayDate}
        </span>
    {/if}
</div>

<NotificationModal
    isModalOpen={isEditModalOpen}
    title="Update Reconciled Date"
    cancelText="Close"
    submitText={isUpdatingReceiveDate ? "Updating" : "Update"}
    isConfirming={isUpdatingReceiveDate}
    closeModal={closeEditModal}
    contentClass="oveflow-x-hidden p-6 flex flex-col"
    confirmAction={updateReceiveDate}
    closeModalFunction={closeEditModal}
>
    <div class="flex flex-col gap-y-4">
        <p class="w-36 text-lg">Reconciled Date:</p>
        <DateInput
            bind:value={newReceiveDate}
            showTime
            wrapperClass="px-0 input w-full max-w-xs input-sm input-bordered"
            wrapperClassFocus="inputFocus"
            minDate={getMinDateOfPayment(
                {
                    receivedDate,
                    transactionDate,
                },
                typeOfPayment,
            )}
        />
    </div>
</NotificationModal>
